import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { Question, Topic, questionApi } from "./api";
import LocationFields from "./LocationFields";
import { fromJurisdiction, sameLocation, toJurisdiction } from "./intake";

export default function QuestionIntakeEditor({
  question,
  topics,
  onSaved,
  onReloaded,
  onCancel,
}: {
  question: Question;
  topics: Topic[];
  onSaved: (question: Question) => void;
  onReloaded: (question: Question) => void;
  onCancel: () => void;
}) {
  const [topic, setTopic] = useState(question.topicKey);
  const [place, setPlace] = useState(fromJurisdiction(question.jurisdiction));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const mounted = useRef(true);
  const currentId = useRef(question.id);
  currentId.current = question.id;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const changed =
    topic !== question.topicKey ||
    !sameLocation(place, fromJurisdiction(question.jurisdiction));
  const validTopic = topics.some(t => t.id === topic);
  useEffect(() => {
    setTopic(question.topicKey);
    setPlace(fromJurisdiction(question.jurisdiction));
    setError("");
    setConflict(false);
    // Intake changes always increment the version; title/history refreshes preserve edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, question.version]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !changed || !validTopic) return;
    const id = question.id;
    setBusy(true);
    setError("");
    setConflict(false);
    try {
      const result = await questionApi<{ question: Question }>(
        `/${question.id}/intake`,
        "PATCH",
        {
          topicKey: topic,
          ...(topic !== question.topicKey ||
          !sameLocation(place, fromJurisdiction(question.jurisdiction))
            ? { jurisdiction: toJurisdiction(topic, place) }
            : {}),
          version: question.version,
        }
      );
      if (mounted.current && currentId.current === id) onSaved(result.question);
    } catch (e: any) {
      if (mounted.current && currentId.current === id) {
        setError(e.message);
        setConflict(e.status === 409);
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <Form onSubmit={save} aria-label="Edit question details">
      <p>
        These details help match a future review. Reviews already submitted keep
        their original topic, location, and terms.
      </p>
      {error && (
        <Alert variant="danger">
          {error}
          {conflict && (
            <Button
              variant="link"
              disabled={busy}
              onClick={async () => {
                const id = question.id;
                setBusy(true);
                try {
                  const result = await questionApi<{ question: Question }>(
                    `/${question.id}`
                  );
                  if (mounted.current && currentId.current === id)
                    onReloaded(result.question);
                } catch (e: any) {
                  if (mounted.current && currentId.current === id)
                    setError(e.message);
                } finally {
                  if (mounted.current) setBusy(false);
                }
              }}
            >
              Reload question details
            </Button>
          )}
        </Alert>
      )}
      <fieldset disabled={busy}>
        <Form.Group controlId="edit-question-topic" className="mb-3">
          <Form.Label>Topic</Form.Label>
          <Form.Select value={topic} onChange={e => setTopic(e.target.value)}>
            {!validTopic && (
              <option value={topic} disabled>
                Current topic (no longer offered)
              </option>
            )}
            {topics.map(t => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <LocationFields
          value={place}
          onChange={setPlace}
          idPrefix="edit-question"
        />
        <div className="d-flex gap-2 mt-3">
          <Button type="submit" disabled={!changed || !validTopic}>
            {busy ? "Saving…" : "Save details"}
          </Button>
          <Button variant="outline-dark" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </fieldset>
    </Form>
  );
}
